import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Callout,
  CopyButton,
  Highlight,
  PagingSidePanelType,
  SidePanel,
  Tabs,
} from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { useCustomerApplicationQuery } from 'hooks/queries/Customers/useCustomerApplicationQuery'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { Application } from 'types/customers'
import { highlightRowBasedOnId } from 'utils/table/table'
import { statusLabelMap, statusVariantMap } from '../utils/utils'
import { CustomerInfoRequired } from './CustomerInfoRequired'
import { CustomerSidePanelBody } from './CustomerSidePanelBody'

export interface ApplicationSidePanelProps {
  isOpen: boolean
  application: Application | undefined
  paging: PagingSidePanelType
  onClose: () => void
}

export const CustomerSidePanel = ({
  isOpen,
  application,
  paging,
  onClose,
}: ApplicationSidePanelProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)

  const onboardingId = application?.id || ''

  const { data, isLoading, isError, refetch } = useCustomerApplicationQuery(onboardingId)

  const onboardingLink = data?.users?.[0]?.link?.link

  const { externalReference, id, status } = application || {}
  const companyName = data?.company?.name || externalReference || id

  return (
    <SidePanel
      id="side-panel-application"
      isOpen={isOpen}
      paging={paging}
      onClose={onClose}
      activeRowId={application?.id}
      onActiveRowChange={highlightRowBasedOnId}
      topOffset={bannerSpacing}
      header={
        <Box flexCol gap={16}>
          <SidePanelHeaderTemplateTwo
            heading={{
              iconUrls: [],
              primaryLabel: <h2 className="text-foreground-prominent text-h2">{companyName}</h2>,
              secondaryLabel: status && (
                <Highlight {...(statusVariantMap[status] && { type: statusVariantMap[status] })}>
                  {statusLabelMap[status] ?? status}
                </Highlight>
              ),
            }}
          />
          {onboardingLink && (
            <SidePanelSection>
              <Callout
                GhostButton={
                  <CopyButton copyText={onboardingLink} testId="copy-onboarding-link">
                    {t('customers.onboardingLink')}
                  </CopyButton>
                }
              >
                {t('customers.onboardingLinkHelp')}
              </Callout>
            </SidePanelSection>
          )}
        </Box>
      }
      body={
        <Box tag="section" width="full" background="standard">
          <Tabs
            tabs={[
              {
                id: 'summary',
                header: t('embeddedCustomers.tabs.summary'),
                content: (
                  <CustomerSidePanelBody
                    application={application}
                    data={data}
                    isLoading={isLoading}
                    isError={isError}
                    refetch={refetch}
                  />
                ),
              },
              {
                id: 'infoRequired',
                header: t('embeddedCustomers.tabs.infoRequired'),
                content: <CustomerInfoRequired onboardingId={onboardingId} />,
              },
            ]}
            defaultTabId="summary"
          />
        </Box>
      }
    />
  )
}
